/**
 * useCampaigns Hook (NEW Architecture - Generic)
 * 
 * Thin wrapper around useList for campaigns.
 * All database operations through CampaignRepository.
 * Zero Firestore imports - fully database-agnostic!
 */

import { useMemo } from 'react';
import { useList } from './useList';
import { CampaignRepository } from '../models/CampaignRepository';
import { db, functions } from '../firebase';

/**
 * Hook for fetching and managing campaign lists
 * 
 * @param {Object} options - Filter and pagination options
 * @param {string} options.statusFilter - Filter by campaign status ('all', 'active', 'archived')
 * @param {string} options.sortOrder - Sort order ('asc' or 'desc')
 * @param {number} options.pageSize - Number of items per page (default: 100)
 * @param {boolean} options.realtime - Enable real-time updates (default: true)
 * 
 * @returns {Object} { campaigns, loading, error, refresh, count, isEmpty }
 */
export const useCampaigns = (options = {}) => {
  const {
    statusFilter = 'all',
    sortOrder = 'desc',
    pageSize = 100,
    realtime = true
  } = options;

  // Create repository instance
  const repository = useMemo(() => new CampaignRepository(db, functions), []);

  // Build query options for repository
  const queryOptions = useMemo(() => {
    const whereConstraints = [];

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        whereConstraints.push(['status', '!=', 'archived']);
      } else {
        whereConstraints.push(['status', '==', statusFilter]);
      }
    }

    return {
      where: whereConstraints.length > 0 ? whereConstraints : undefined,
      orderBy: 'createdAt',
      orderDirection: sortOrder,
      limit: pageSize
    };
  }, [statusFilter, sortOrder, pageSize]);

  // Use generic list hook
  const {
    entities: campaigns,
    loading,
    error,
    refresh,
    count,
    isEmpty
  } = useList({
    repository,
    options: queryOptions,
    realtime,
    immediate: true
  });

  return {
    campaigns,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError: !!error
  };
};
